import React, { useEffect, useState } from "react";
import StudentListing from "./StudentListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/StudentSlice";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import AddStudentWrapper from "../Add/AddStudentWrapper";
import EditStudentWrapper from "../Edit/EditStudentWrapper";
import {
  useChangeStatusStudentByIdMutation,
  useDeleteStudentByIdMutation,
  useGetAllStudentDataQuery,
} from "src/services/StudentService";
import { usePermission } from "src/hooks/usePermission";
import ListDateTime from "src/components/UI/atoms/ListDateTime/ListDateTime";

type Props = {};

const StudentListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isOpenAddDialog,
    isOpenEditDialog,
    isTableLoading,
  } = useSelector((state: RootState) => state.student);
  const [selectedStudentId, setSelectedStudentId] = useState<string>();
  const [deleteStudent] = useDeleteStudentByIdMutation();
  const [changeStatusStudent] = useChangeStatusStudentByIdMutation();

  const { data, isLoading, isFetching } = useGetAllStudentDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["name", "mobile"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  // Setting Module Data
  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const handleDeleteStudent = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this Student ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteStudent(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusStudent = (row: any) => {
    const actionLabel = row?.isActive ? "deactive" : "active";
    showConfirmationDialog({
      title: "Hands Up",
      text: `Are you sure want to ${actionLabel} this Student ?`,
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusStudent({ id: row?._id }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const canEdit = usePermission("STUDENT", "UPDATE");
  const canDelete = usePermission("STUDENT", "DELETE");
  const canActive = usePermission("STUDENT", "ACTIVE/DEACTIVE");
  const columns: columnTypes[] = [
    {
          field: "createdAt",
          headerName: "Date - Time",
    
          extraClasses: "min-w-[150px] py-1 min-w-[150px]",
          flex: "flex-[1_1_0%]",
          renderCell: (row: any) => <ListDateTime value={row.createdAt} />,
        },
    { field: "name", headerName: "Student Name", flex: "flex-[1_1_0%]" },
    { field: "mobile", headerName: "Mobile No.", flex: "flex-[1_1_0%]" },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      renderCell: (row: any) => {
        const menuOptions = [];

        if (canEdit) {
          menuOptions.push({
            label: "Edit",
            onClick: () => {
              dispatch(setIsOpenEditDialog(true));
              setSelectedStudentId(row?._id);
            },
          });
        }

        if (canActive) {
          menuOptions.push({
            label: row?.isActive ? "Deactive" : "Active",
            onClick: () => handleChangeStatusStudent(row),
          });
        }

        if (canDelete) {
          menuOptions.push({
            label: "Delete",
            onClick: () => handleDeleteStudent(row?._id),
          });
        }

        return <ATMMenu options={menuOptions} />;
      },
    },
  ];

  return (
    <>
      <div>
        <StudentListing
          columns={columns}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setIsOpenAddDialog: (value) => dispatch(setIsOpenAddDialog(value)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenAddDialog ? (
        <AddStudentWrapper
          onClose={() => dispatch(setIsOpenAddDialog(false))}
        />
      ) : null}
      {isOpenEditDialog ? (
        <EditStudentWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedStudentId={selectedStudentId || ""}
        />
      ) : null}
    </>
  );
};

export default StudentListingWrapper;
